import { useEffect, useRef } from "react";
import { useUser } from "@webuild-ai/react-auth";

import { useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { logger } from "@/telemetry";

const SYNC_DEBOUNCE_MS = 2000;

type ToolPreferences = Record<string, Record<string, string[]>>;

/** Structural equality check for ToolPreferences objects (key-order independent). */
function arePreferencesEqual(a: ToolPreferences, b: ToolPreferences): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const wfId of aKeys) {
    if (!(wfId in b)) return false;

    const aAgents = a[wfId];
    const bAgents = b[wfId];
    const aAgentKeys = Object.keys(aAgents);
    const bAgentKeys = Object.keys(bAgents);

    if (aAgentKeys.length !== bAgentKeys.length) return false;

    for (const agentId of aAgentKeys) {
      if (!(agentId in bAgents)) return false;

      const aTools = aAgents[agentId];
      const bTools = bAgents[agentId];

      if (aTools.length !== bTools.length) return false;

      for (let i = 0; i < aTools.length; i++) {
        if (aTools[i] !== bTools[i]) return false;
      }
    }
  }

  return true;
}

/**
 * Syncs tool preferences between localStorage (ToolPreferencesStore) and the
 * backend UserConfig.
 *
 * - On first profile load: seeds localStorage from backend preferences (cross-device roaming).
 * - On tool toggle: debounces writes to the backend (2s after last change).
 */
export function useToolPreferenceSync(): void {
  const { adminClient } = useWebuildAPIClient();
  const { userProfile } = useUser();
  const preferences = useToolPreferencesStore((s) => s.preferences);
  const bootstrappedUserId = useRef<string | null>(null);
  const lastSyncedPrefs = useRef<ToolPreferences>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Bootstrap: seed localStorage from backend on first profile load (or when user changes)
  useEffect(() => {
    if (!userProfile?.config || bootstrappedUserId.current === userProfile.id) return;
    bootstrappedUserId.current = userProfile.id;

    // Access tool_preferences from backend (may not be in react-auth types yet)
    const backendPrefs =
      (userProfile.config as Record<string, unknown>).toolPreferences ??
      (userProfile.config as Record<string, unknown>).tool_preferences ??
      {};

    if (
      typeof backendPrefs === "object" &&
      backendPrefs !== null &&
      Object.keys(backendPrefs).length > 0
    ) {
      useToolPreferencesStore.getState().loadFromBackend(backendPrefs as ToolPreferences);
    }

    // Mark the post-bootstrap state as synced so we don't immediately write it back
    lastSyncedPrefs.current = useToolPreferencesStore.getState().preferences;
  }, [userProfile]);

  // Debounced sync to backend after preference changes
  useEffect(() => {
    if (!adminClient || !userProfile) return;

    if (arePreferencesEqual(lastSyncedPrefs.current, preferences)) return;

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const currentPrefs = useToolPreferencesStore.getState().preferences;

        await adminClient.users.updateUser({
          userId: userProfile.id,
          userInput: {
            ...userProfile,
            config: {
              ...(userProfile.config || {}),
              toolPreferences: currentPrefs,
              // TODO(NGVC): Remove tool_preferences once backend standardises on camelCase
              tool_preferences: currentPrefs,
            },
            // TODO: Remove cast once @webuild-ai/react-auth types include toolPreferences in UserConfig
          } as any,
        });
        lastSyncedPrefs.current = currentPrefs;
        logger.info("Tool preferences synced to backend");
      } catch (error) {
        logger.warn("Failed to sync tool preferences to backend", {
          error: String(error),
        });
      }
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [preferences, adminClient, userProfile]);
}
